import { w as writable } from './common/index-07acef41.js';
import { g as get_store_value } from './common/index-915971b4.js';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __values(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

/**
 * Extract all fields from the form that are valid inputs with `name` property that are not part of a form group
 *
 * @private
 * @internal
 * @param rootEl
 */
function getFormFields(rootEl) {
  var nodeList = rootEl.querySelectorAll('*[name]:not([data-in-group])');
  return Array.from(nodeList).filter(function (el) {
    return el.checkValidity;
  });
}
/**
 * Extract all fields from a group that are valid inputs with `name` property
 * @param rootEl
 */

function getGroupFields(rootEl) {
  var nodeList = rootEl.querySelectorAll('*[name]');
  return Array.from(nodeList).filter(function (el) {
    return el.checkValidity;
  });
}

/**
 * The object returned by the {@link https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/HTML5/Constraint_validation|Contraints Validation API} cannot
 * be enumerated, so we need to loop over the keys to extract them
 *
 * The key object is merged with any custom errors
 *
 * @private
 *
 * @param el The elements to read the constraints from
 * @param custom Custom error keys
 *
 * @returns An object containing keys for validity errors, set to true
 */

function extractErrors(el, custom) {
  var output = {};

  for (var key in el.validity) {
    if (key !== 'valid' && el.validity[key]) {
      output[key] = el.validity[key];
    }
  }

  return __assign(__assign({}, output), custom);
}
/**
 * Get the result of any custom validations available on the fields.
 * @param value
 * @param validations
 */


function getCustomValidations(value, validations) {
  if (validations === void 0) {
    validations = {};
  }

  var messages = {};
  var errors = {};
  Object.entries(validations).forEach(function (_a) {
    var _b = __read(_a, 2),
        key = _b[0],
        validation = _b[1];

    var message = validation(value);

    if (message !== null) {
      messages[key] = message;
      errors[key] = true;
    }
  });
  return [messages, errors];
}
/**
 * Create a validation checker for an element group - when an element has been updated.  If there are no options
 * just return the element validity. If there are options, check with custom validators if thet exist,
 * and also check for custom messages
 *
 * @private
 *
 * @param inputGroup The name of the group of elements that this validation message will update
 * @param elementGroup The element group containing the elment siblings
 * @param options The passed formula options
 *
 * @returns Function that is called each time an element is updated which returns field validity state
 */


function createValidationChecker(inputGroup, elementGroup, options) {
  /**
   * Method called each time a field is updated
   *
   * @private
   *
   * @param el The element to validate against
   * @param elValue The value for the element
   *
   * @returns A Formula Error object
   */
  return function (el, elValue) {
    var _a, _b; // Reset the validity


    elementGroup.forEach(function (gel) {
      gel.setCustomValidity('');
      gel.removeAttribute('data-formula-invalid');
    }); // If there's no options, just return the current error

    if (!options) {
      var valid_1 = el.checkValidity();

      if (!valid_1) {
        el.setAttribute('data-formula-invalid', 'true');
      }

      return {
        valid: valid_1,
        invalid: !valid_1,
        message: el.validationMessage,
        errors: extractErrors(el)
      };
    } // Check for any custom messages in the options or dataset


    var customMessages = __assign(__assign({}, (_a = options === null || options === void 0 ? void 0 : options.messages) === null || _a === void 0 ? void 0 : _a[inputGroup]), el.dataset); // Check for any custom validations


    var _c = __read(getCustomValidations(elValue, (_b = options === null || options === void 0 ? void 0 : options.validators) === null || _b === void 0 ? void 0 : _b[inputGroup]), 2),
        messages = _c[0],
        customErrors = _c[1];

    var errors = extractErrors(el, customErrors);
    var errorKeys = Object.keys(errors); // If there is no field validity issues, set custom ones

    if (el.checkValidity()) {
      if (errorKeys.length > 0) {
        el.setCustomValidity(messages[errorKeys[0]]);
      } // Check for custom messages

    } else {
      if (customMessages[errorKeys[0]]) {
        el.setCustomValidity(customMessages[errorKeys[0]]);
      }
    } // Recheck validity and show any messages


    var valid = el.checkValidity();

    if (!valid) {
      el.setAttribute('data-formula-invalid', 'true');
    }

    return {
      valid: valid,
      invalid: !valid,
      message: el.validationMessage,
      errors: errors
    };
  };
}

/**
 * Go up from the current element to the parent to find the group container, but never go further than the current
 * group or form
 * @param el
 */

function getRadioGroupParent(el) {
  var _a, _b;

  if (!el) {
    return undefined;
  }

  var isContainer = Array.from(el.querySelectorAll(':scope input[type=radio]')).length > 1;

  if (!isContainer) {
    if (!el.parentElement) {
      return undefined;
    }

    if (((_a = el.parentElement.dataset) === null || _a === void 0 ? void 0 : _a.beakerGroup) || ((_b = el.parentElement.dataset) === null || _b === void 0 ? void 0 : _b.formulaForm)) {
      return undefined;
    }

    return getRadioGroupParent(el === null || el === void 0 ? void 0 : el.parentElement);
  }

  return el;
}
/**
 * Sets the ARIA role based on the input type
 * @param el
 * @param elements
 */


function setAriaRole(el, elements) {
  if (el.hasAttribute('aria-role')) {
    return;
  }

  if (el.type === 'radio') {
    if (elements.length < 2) {
      el.parentElement.setAttribute('aria-role', 'radiogroup');
    } else {
      var radioGroup = getRadioGroupParent(el.parentElement);
      if (radioGroup) radioGroup.setAttribute('aria-role', 'radiogroup');
    }

    el.setAttribute('aria-role', 'radio');
  } else {
    el.setAttribute('aria-role', function () {
      switch (el.type) {
        case 'select-one':
        case 'select-multiple':
        case 'checkbox':
          {
            return el.type;
          }

        case 'file':
          {
            return 'file-upload';
          }

        case 'textarea':
          {
            return 'textbox';
          }

        default:
          return "input-" + el.type;
      }
    }());
  }
}
/**
 * Sets ARIA states based on attributes on the form
 * @param el
 */

function setAriaStates(el) {
  if (el.hasAttribute('required')) {
    el.setAttribute('aria-required', 'required');
  }
}
/**
 * Sets ARIA attributes based on the current value
 * @param element
 * @param elGroup
 */

function setAriaValue(element, elGroup) {
  var e_1, _a;

  if (element.type === 'radio') {
    try {
      for (var elGroup_1 = __values(elGroup), elGroup_1_1 = elGroup_1.next(); !elGroup_1_1.done; elGroup_1_1 = elGroup_1.next()) {
        var el = elGroup_1_1.value;
        el.removeAttribute('aria-checked');
      }
    } catch (e_1_1) {
      e_1 = {
        error: e_1_1
      };
    } finally {
      try {
        if (elGroup_1_1 && !elGroup_1_1.done && (_a = elGroup_1["return"])) _a.call(elGroup_1);
      } finally {
        if (e_1) throw e_1.error;
      }
    }
  }

  if (element.checked) {
    element.setAttribute('aria-checked', 'checked');
  } else {
    element.removeAttribute('aria-checked');
  }
}
/**
 * Set the container
 * @param container
 * @param isGroup
 */

function setAriaContainer(container, isGroup) {
  if (!container.hasAttribute('aria-role')) {
    container.setAttribute('aria-role', isGroup ? 'row' : 'form');
  }
}
/**
 * Add the ARIA button role to all buttons contained in the form that don't already have an ARIA role
 * @param container
 */

function setAriaButtons(container) {
  var e_2, _a;

  var nonAriaButtons = Array.from(container.querySelectorAll('button:not([aria-role])'));

  try {
    for (var nonAriaButtons_1 = __values(nonAriaButtons), nonAriaButtons_1_1 = nonAriaButtons_1.next(); !nonAriaButtons_1_1.done; nonAriaButtons_1_1 = nonAriaButtons_1.next()) {
      var el = nonAriaButtons_1_1.value;
      el.setAttribute('aria-role', 'button');
    }
  } catch (e_2_1) {
    e_2 = {
      error: e_2_1
    };
  } finally {
    try {
      if (nonAriaButtons_1_1 && !nonAriaButtons_1_1.done && (_a = nonAriaButtons_1["return"])) _a.call(nonAriaButtons_1);
    } finally {
      if (e_2) throw e_2.error;
    }
  }
}

/**
 * As the `HTMLCollectionOf` is not iterable, we have to loop over it with
 * a for loop instead
 * @private
 * @internal
 * @param collection
 */

function getMultiSelectOptionValues(collection) {
  var value = [];

  for (var i = 0; i < collection.length; i++) {
    if (collection[i].selected) {
      value.push(collection[i].value);
    }
  }

  return value;
}
/**
 * Sets the element value
 * @param element
 * @param value
 * @param isMultiValue
 * @param elementGroup
 */


function setElementValue(element, value, isMultiValue, elementGroup) {
  if (isMultiValue) {
    elementGroup.forEach(function (el, i) {
      if (el.type === 'checkbox') {
        el.checked = value.includes(el.value);
      } else {
        el.value = value[i];
      }
    });
  } else {
    if (element instanceof HTMLSelectElement) {
      for (var i = 0; i < element.options.length; i++) {
        var el = element.options[i];
        el.selected = value.includes(el.value);
      }
    } else if (element.type === 'checkbox') {
      element.checked = value;
    } else if (element.type === 'radio') {
      elementGroup.forEach(function (el) {
        return el.checked = value === el.value;
      });
    } else if (element.type === 'file') {
      element.files = value instanceof FileList ? value : null;
    } else {
      element.value = value;
    }
  }
}
/**
 * Get the value or values from an element
 * @param element
 * @param isMultiValue
 * @param elementGroup
 */


function getElementValues(element, isMultiValue, elementGroup) {
  var elValue;

  if (element instanceof HTMLSelectElement) {
    elValue = element.multiple ? getMultiSelectOptionValues(element.options) : element.value || null;
  } else {
    switch (element.type) {
      case 'number':
      case 'range':
        {
          elValue = isMultiValue ? elementGroup.map(function (v) {
            return v.id === element.id ? parseFloat(element.value) : parseFloat(v.value);
          }).filter(function (v) {
            return !isNaN(v);
          }) : function () {
            var val = parseFloat(element.value);
            return !isNaN(val) ? val : null;
          }();
          break;
        }

      case 'checkbox':
        {
          elValue = isMultiValue ? elementGroup.map(function (e) {
            return e.id === element.id ? element.checked && element.value : e.checked && e.value;
          }).filter(Boolean) : element.checked;
          break;
        }

      case 'radio':
        {
          var foundElement = elementGroup.find(function (el) {
            return el.checked;
          });
          elValue = foundElement ? foundElement.value : null;
          break;
        }

      case 'file':
        {
          elValue = element.files;
          break;
        }

      default:
        {
          elValue = isMultiValue ? elementGroup.map(function (v) {
            return v.id === element.id ? element.value : v.value;
          }) : element.value || null;
        }
    }
  }

  return elValue;
}
/**
 * Create a data handler for any type of input field
 * @param name
 * @param elementGroup
 * @param options
 * @param stores
 */


function createFieldExtract(name, elementGroup, options, stores) {
  var validator = createValidationChecker(name, elementGroup, options);

  var isMultiValue = function () {
    return elementGroup[0].type === 'radio' ? false : !elementGroup[0].multiple;
  }() && elementGroup.length > 1;
  /**
   * Function called on every element update, can also be called at initial value
   * Welcome to edge-case hell
   */


  return function (element, isInit, isReset) {
    var _a, _b, _c, _d, _e;

    var value;

    if (isInit && ((_a = options === null || options === void 0 ? void 0 : options.defaultValues) === null || _a === void 0 ? void 0 : _a[name])) {
      value = isMultiValue ? ((_b = options === null || options === void 0 ? void 0 : options.defaultValues) === null || _b === void 0 ? void 0 : _b[name]) || [] : ((_c = options === null || options === void 0 ? void 0 : options.defaultValues) === null || _c === void 0 ? void 0 : _c[name]) || '';
    } else {
      var storeValue = (_d = get_store_value(stores.formValues)) === null || _d === void 0 ? void 0 : _d[name];
      value = storeValue ? storeValue : isMultiValue ? [] : '';
    }

    if (!isReset) {
      var elValue = getElementValues(element, isMultiValue, elementGroup);

      if (isInit) {
        if (isMultiValue || element.type === 'select-multiple') {
          value = elValue.length === 0 ? value : elValue;
        } else if (!value) {
          value = elValue === null ? '' : elValue;
        }
      } else {
        value = elValue === null ? '' : elValue;
      }
    }

    if (isInit || isReset) {
      setElementValue(element, value, isMultiValue, elementGroup);
    }

    setAriaValue(element, elementGroup);

    if ((_e = element.dataset) === null || _e === void 0 ? void 0 : _e.formulaName) {
      name = element.dataset.formulaName;
    }

    return __assign({
      name: name,
      value: value
    }, validator(element, value));
  };
}

/**
 * Creates an enrichment object for the named group,
 * @param name
 * @param options
 */

function createEnrichField(name, options) {
  return function (value) {
    return Object.entries(options.enrich[name]).reduce(function (a, _a) {
      var _b = __read(_a, 2),
          key = _b[0],
          fn = _b[1];

      a[key] = fn(value);
      return a;
    }, {});
  };
}

/**
 * Do validation on the form and set the form validity state and set the form to invalid if there
 * are any form validations
 * @param formValidators
 * @param stores
 */

function formValidation(formValidators, stores) {
  var currentValues = get_store_value(stores.formValues);
  stores.formValidity.set({});
  var validators = Object.entries(formValidators);
  var invalidStates = {};

  for (var i = 0; i < validators.length; i++) {
    var _a = __read(validators[i], 2),
        name_1 = _a[0],
        validator = _a[1];

    var invalid = validator(currentValues);

    if (invalid !== null) {
      invalidStates[name_1] = invalid;
    }
  }

  if (Object.keys(invalidStates).length > 0) {
    stores.formValidity.set(invalidStates);
    stores.isFormValid.set(false);
  }
}
/**
 * Update the value and error stores, also update form validity
 * @param details
 * @param stores
 * @param options
 * @param hiddenFields
 * @param enrich
 */


function valueUpdate(details, stores, options, hiddenFields, enrich) {
  var _a;

  var name = details.name,
      value = details.value,
      validity = __rest(details, ["name", "value"]);

  stores.formValues.update(function (state) {
    var _a;

    return __assign(__assign({}, state), (_a = {}, _a[name] = value, _a));
  });

  if (hiddenFields.size) {
    stores.formValues.update(function (state) {
      hiddenFields.forEach(function (group, name) {
        return state[name] = group.length > 1 ? group.map(function (e) {
          return e.value;
        }) : group[0].value;
      });
      return state;
    });
  } // Update validity and form validity


  stores.validity.update(function (state) {
    var _a;

    return __assign(__assign({}, state), (_a = {}, _a[name] = validity, _a));
  });
  stores.isFormValid.set(Object.values(get_store_value(stores.validity)).every(function (v) {
    return v.valid;
  }));

  if (options === null || options === void 0 ? void 0 : options.formValidators) {
    formValidation(options.formValidators, stores);
  }

  if (enrich) {
    stores.enrichment.set((_a = {}, _a[name] = enrich(value), _a));
  }

  if (typeof (options === null || options === void 0 ? void 0 : options.postChanges) === 'function') {
    options === null || options === void 0 ? void 0 : options.postChanges(get_store_value(stores.formValues));
  }
}
/**
 * Creates an event handler for the passed element with it's data handler
 * @param extractor
 * @param stores
 * @param options
 * @param hiddenFields
 * @param enrich
 */

function createHandlerForData(extractor, stores, options, hiddenFields, enrich) {
  return function (event) {
    if (typeof (options === null || options === void 0 ? void 0 : options.preChanges) === 'function') options.preChanges(); // Allow elements to update by letting the browser do a tick

    setTimeout(function () {
      var el = event.currentTarget || event.target;
      valueUpdate(extractor(el), stores, options, hiddenFields, enrich);
    }, 0);
  };
}
/**
 * Create a handler for the passed element
 * @param name
 * @param eventName
 * @param element
 * @param groupElements
 * @param stores
 * @param options
 * @param hiddenGroups
 */


function createHandler(name, eventName, element, groupElements, stores, options, hiddenGroups) {
  var _a;

  var extract = createFieldExtract(name, groupElements, options, stores);
  var enrich;
  if ((_a = options === null || options === void 0 ? void 0 : options.enrich) === null || _a === void 0 ? void 0 : _a[name]) enrich = createEnrichField(name, options);
  var handler = createHandlerForData(extract, stores, options, hiddenGroups, enrich);
  element.addEventListener(eventName, handler);
  return function () {
    return element.removeEventListener(eventName, handler);
  };
}
/**
 * Create a handler for a form element submission, when called it copies the contents
 * of the current value store to the submit store and then unsubscribes
 * @param stores
 * @param form
 */

function createSubmitHandler(stores, form) {
  return function () {
    if (!form.noValidate) form.reportValidity();
    stores.formValues.subscribe(function (v) {
      return stores.submitValues.set(v);
    })();
  };
}

/**
 * Initialise the stores with data from the form, it will also use any default values provided
 * @param node
 * @param allGroups
 * @param stores
 * @param options
 */

function getInitialFormValues(node, allGroups, stores, options) {
  var e_1, _a;

  var _b;

  var formValues = {};
  var validityValues = {};
  var enrichmentValues = {};

  try {
    for (var allGroups_1 = __values(allGroups), allGroups_1_1 = allGroups_1.next(); !allGroups_1_1.done; allGroups_1_1 = allGroups_1.next()) {
      var _c = __read(allGroups_1_1.value, 2),
          key = _c[0],
          elements = _c[1];

      var extract = createFieldExtract(key, elements, options, stores);

      var _d = extract(elements[0], true),
          name_1 = _d.name,
          value = _d.value,
          validity = __rest(_d, ["name", "value"]);

      formValues[name_1] = value;
      validityValues[name_1] = validity;

      if ((_b = options === null || options === void 0 ? void 0 : options.enrich) === null || _b === void 0 ? void 0 : _b[name_1]) {
        var enrich = createEnrichField(name_1, options);
        enrichmentValues[name_1] = enrich(value);
      }
    }
  } catch (e_1_1) {
    e_1 = {
      error: e_1_1
    };
  } finally {
    try {
      if (allGroups_1_1 && !allGroups_1_1.done && (_a = allGroups_1["return"])) _a.call(allGroups_1);
    } finally {
      if (e_1) throw e_1.error;
    }
  }

  stores.formValues.set(__assign({}, formValues));
  stores.initialValues.set(__assign({}, formValues));
  stores.validity.set(__assign({}, validityValues));
  stores.isFormValid.set(Object.values(__assign({}, validityValues)).every(function (v) {
    return v.valid;
  }));
  stores.enrichment.set(__assign({}, enrichmentValues));
  return [formValues, validityValues, enrichmentValues];
}
/**
 * Create the form reset method

 */


function createReset(node, allGroups, stores, options) {
  var _a = __read(getInitialFormValues(node, allGroups, stores, options), 3),
      formValues = _a[0],
      validityValues = _a[1],
      enrichmentValues = _a[2];
  /**
   * Resets the form to the initial values
   */


  return function () {
    var e_2, _a;

    stores.formValues.set(formValues);
    stores.validity.set(validityValues);
    stores.isFormValid.set(Object.values(validityValues).every(function (v) {
      return v.valid;
    }));
    stores.enrichment.set(enrichmentValues); // Also override touched and dirty

    stores.touched.set(Object.keys(formValues).reduce(function (val, key) {
      var _a;

      return __assign(__assign({}, val), (_a = {}, _a[key] = false, _a));
    }, {}));
    stores.dirty.set(Object.keys(formValues).reduce(function (val, key) {
      var _a;

      return __assign(__assign({}, val), (_a = {}, _a[key] = false, _a));
    }, {}));

    try {
      // Update the elements
      for (var allGroups_2 = __values(allGroups), allGroups_2_1 = allGroups_2.next(); !allGroups_2_1.done; allGroups_2_1 = allGroups_2.next()) {
        var _b = __read(allGroups_2_1.value, 2),
            key = _b[0],
            elements = _b[1];

        var extract = createFieldExtract(key, elements, options, stores);
        extract(elements[0], false, true);
      }
    } catch (e_2_1) {
      e_2 = {
        error: e_2_1
      };
    } finally {
      try {
        if (allGroups_2_1 && !allGroups_2_1.done && (_a = allGroups_2["return"])) _a.call(allGroups_2);
      } finally {
        if (e_2) throw e_2.error;
      }
    }
  };
}

/**
 * Creates the handler for a group of elements for the touch event on the group name, once an
 * element in the group has been touched, all element focus handlers will be removed
 *
 * @private
 *
 * @param name The name of the group to create the touch handlers for
 * @param elements The elements that belong to the named group
 * @param stores The stores for the form instance
 *
 * @returns Function that when called will remove all focus handlers from the elements, if not removed by user action
 */

function createTouchHandlers(name, elements, stores) {
  var e_1, _a;
  /**
   * Internal map of element focus handlers
   */


  var elementHandlers = new Map();
  /**
   * Method to call when handlers should be destroyed, clean up handlers then remove the handlers
   */

  var destroy = function destroy() {
    var e_2, _a;

    try {
      for (var elementHandlers_1 = __values(elementHandlers), elementHandlers_1_1 = elementHandlers_1.next(); !elementHandlers_1_1.done; elementHandlers_1_1 = elementHandlers_1.next()) {
        var _b = __read(elementHandlers_1_1.value, 2),
            el = _b[0],
            handler = _b[1];

        el.setAttribute('data-formula-touched', 'true');
        el.removeEventListener('focus', handler);
      }
    } catch (e_2_1) {
      e_2 = {
        error: e_2_1
      };
    } finally {
      try {
        if (elementHandlers_1_1 && !elementHandlers_1_1.done && (_a = elementHandlers_1["return"])) _a.call(elementHandlers_1);
      } finally {
        if (e_2) throw e_2.error;
      }
    }

    elementHandlers.clear();
  }; // Set the current touched state to false


  stores.touched.update(function (state) {
    var _a;

    return __assign(__assign({}, state), (_a = {}, _a[name] = false, _a));
  });
  /**
   * Creates the instance of the handler for each element
   */

  function createElementHandler() {
    return function () {
      stores.touched.update(function (state) {
        var _a;

        return __assign(__assign({}, state), (_a = {}, _a[name] = true, _a));
      });
      destroy();
    };
  }

  try {
    for (var elements_1 = __values(elements), elements_1_1 = elements_1.next(); !elements_1_1.done; elements_1_1 = elements_1.next()) {
      var el = elements_1_1.value;
      var handler = createElementHandler();
      el.addEventListener('focus', handler);
      elementHandlers.set(el, handler);
    }
  } catch (e_1_1) {
    e_1 = {
      error: e_1_1
    };
  } finally {
    try {
      if (elements_1_1 && !elements_1_1.done && (_a = elements_1["return"])) _a.call(elements_1);
    } finally {
      if (e_1) throw e_1.error;
    }
  }

  return destroy;
}

/**
 * Check if two arrays match
 * @param array1
 * @param array2
 */

var matchingArrays = function matchingArrays(array1, array2) {
  return array1.every(function (e) {
    return array2.includes(e);
  });
};
/**
 * Creates the handler for a group of elements for the dirty event on the group name, once an
 * element in the group has been changed, all element blur handlers will be removed
 *
 * @private
 *
 * @param name The name of the group to create the blur handlers for
 * @param elements The elements that belong to the named group
 * @param stores The stores for the form instance
 *
 * @returns Function that when called will remove all blur handlers from the elements, if not removed by user action
 */


function createDirtyHandler(name, elements, stores) {
  var e_1, _a;
  /**
   * Internal map of element blur handlers
   */


  var elementHandlers = new Map();
  /**
   * Internal map of initial values
   */

  var initialValues = new Map();

  var setDirtyAndStopListening = function setDirtyAndStopListening() {
    var e_2, _a;

    try {
      for (var elementHandlers_1 = __values(elementHandlers), elementHandlers_1_1 = elementHandlers_1.next(); !elementHandlers_1_1.done; elementHandlers_1_1 = elementHandlers_1.next()) {
        var _b = __read(elementHandlers_1_1.value, 2),
            el = _b[0],
            handler = _b[1];

        el.setAttribute('data-formula-dirty', 'true');
        el.removeEventListener('blur', handler);
      }
    } catch (e_2_1) {
      e_2 = {
        error: e_2_1
      };
    } finally {
      try {
        if (elementHandlers_1_1 && !elementHandlers_1_1.done && (_a = elementHandlers_1["return"])) _a.call(elementHandlers_1);
      } finally {
        if (e_2) throw e_2.error;
      }
    }

    elementHandlers.clear();
  }; // Set initial dirty state and initial value


  stores.dirty.update(function (state) {
    var _a;

    return __assign(__assign({}, state), (_a = {}, _a[name] = false, _a));
  });
  stores.formValues.subscribe(function (v) {
    return initialValues.set(name, v[name]);
  })();

  function createElementHandler(groupName) {
    return function () {
      var startValue = initialValues.get(groupName);
      var currentValues = get_store_value(stores.formValues);

      if (Array.isArray(currentValues[groupName])) {
        if (!matchingArrays(currentValues[groupName], startValue)) {
          stores.dirty.update(function (state) {
            var _a;

            return __assign(__assign({}, state), (_a = {}, _a[groupName] = true, _a));
          });
          setDirtyAndStopListening();
        }
      } else if (currentValues[groupName] !== startValue) {
        stores.dirty.update(function (state) {
          var _a;

          return __assign(__assign({}, state), (_a = {}, _a[groupName] = true, _a));
        });
        setDirtyAndStopListening();
      }
    };
  }

  try {
    for (var elements_1 = __values(elements), elements_1_1 = elements_1.next(); !elements_1_1.done; elements_1_1 = elements_1.next()) {
      var el = elements_1_1.value;
      var handler = createElementHandler(name);
      el.addEventListener('blur', handler);
      elementHandlers.set(el, handler);
    }
  } catch (e_1_1) {
    e_1 = {
      error: e_1_1
    };
  } finally {
    try {
      if (elements_1_1 && !elements_1_1.done && (_a = elements_1["return"])) _a.call(elements_1);
    } finally {
      if (e_1) throw e_1.error;
    }
  }

  return setDirtyAndStopListening;
}

/**
 * Function to create initial state values for the store using any passed default values, this is not the final initial
 * state, but is used when the stores are created to ensure required keys are available with any initial state, even if
 * an empty string.
 *
 * This avoids the need for the `?.` operator in code since we can guarantee the key on first subscription this way
 *
 * @private
 * @internal
 *
 * @param options Initial options to use
 * @param initialData
 */

function createFirstState(options, initialData) {
  var initialValues = (options === null || options === void 0 ? void 0 : options.defaultValues) || {};
  initialValues = __assign(__assign({}, initialValues), initialData);
  var initialKeys = Object.keys(initialValues); // Generate from default values any initial touched and dirty states

  var initialFieldState = initialKeys.reduce(function (val, key) {
    var _a;

    return __assign(__assign({}, val), (_a = {}, _a[key] = false, _a));
  }, {}); // Generate from default values any initial initial validity

  var initialValidity = initialKeys.reduce(function (val, key) {
    var _a;

    return __assign(__assign({}, val), (_a = {}, _a[key] = {
      valid: true,
      invalid: false,
      message: '',
      errors: {}
    }, _a));
  }, {}); // Generate from default values any initial form validity

  var initialFormValidity = Object.keys((options === null || options === void 0 ? void 0 : options.formValidators) || {}).reduce(function (val, key) {
    var _a;

    return __assign(__assign({}, val), (_a = {}, _a[key] = '', _a));
  }, {}); // Generate from default values any initial enrichment

  var initialEnrichment = Object.entries((options === null || options === void 0 ? void 0 : options.enrich) || {}).reduce(function (value, _a) {
    var _b;

    var _c = __read(_a, 2),
        key = _c[0],
        fns = _c[1];

    return __assign(__assign({}, value), (_b = {}, _b[key] = Object.entries(fns).reduce(function (v, _a) {
      var _b;

      var _c, _d;

      var _e = __read(_a, 2),
          k = _e[0],
          fn = _e[1];

      return __assign(__assign({}, v), (_b = {}, _b[k] = ((_c = options === null || options === void 0 ? void 0 : options.defaultValues) === null || _c === void 0 ? void 0 : _c[key]) ? fn((_d = options === null || options === void 0 ? void 0 : options.defaultValues) === null || _d === void 0 ? void 0 : _d[key]) : undefined, _b));
    }, {}), _b));
  }, {});
  return {
    initialValues: initialValues,
    initialKeys: initialKeys,
    initialFieldState: initialFieldState,
    initialValidity: initialValidity,
    initialFormValidity: initialFormValidity,
    initialEnrichment: initialEnrichment
  };
}
/**
 * Create the stores for the the form instance, these can be set using the `defaultValue` property
 * of FormulaOptions
 * @param options
 * @param initialData
 *
 * @returns An object containing the stores for the form instance
 */


function createFormStores(options, initialData) {
  var initialStoreState = createFirstState(options, initialData);
  return {
    formValues: writable(initialStoreState.initialValues),
    submitValues: writable({}),
    initialValues: writable(initialStoreState.initialValues),
    touched: writable(initialStoreState.initialFieldState),
    dirty: writable(initialStoreState.initialFieldState),
    validity: writable(initialStoreState.initialValidity),
    formValidity: writable(initialStoreState.initialFormValidity),
    isFormValid: writable(false),
    isFormReady: writable(false),
    enrichment: writable(initialStoreState.initialEnrichment)
  };
}

/**
 * Creates the form action
 * @param options
 * @param globalStore
 * @param groupName
 * @param initialData
 */

function createForm(options, globalStore, groupName, initialData) {
  /**
   * Store for all keyup handlers than need removed when destroyed
   */
  var eventHandlers = new Map();
  var hiddenGroups = new Map();
  var touchHandlers = new Set();
  var dirtyHandlers = new Set();
  var stores = createFormStores(options, initialData);
  var isGroup = typeof groupName !== 'undefined';
  var initialOptions = options;
  var submitHandler = undefined;
  var innerReset;
  var groupedMap = [];
  /**
   * Internal method to do binding of te action element
   * @param node
   * @param innerOpt
   */

  function bindElements(node, innerOpt) {
    var formElements = isGroup ? getGroupFields(node) : getFormFields(node);
    node.setAttribute("data-formula-" + (isGroup ? 'row' : 'form'), 'true');
    setAriaContainer(node, isGroup);
    setAriaButtons(node); // Group elements by name

    groupedMap = __spread(formElements.reduce(function (entryMap, e) {
      var formulaName = e.dataset.formulaName;
      var name = formulaName || e.getAttribute('name');
      return entryMap.set(name, __spread(entryMap.get(name) || [], [e]));
    }, new Map()));
    innerReset = createReset(node, groupedMap, stores, innerOpt); // Loop over each group and setup up their initial touch and dirty handlers,
    // also get initial values

    groupedMap.forEach(function (_a) {
      var _b = __read(_a, 2),
          name = _b[0],
          elements = _b[1]; // Special case for hidden fields, put into the hidden set to pass only on form submission
      // as they don't react to event anyway


      if (elements[0].type === 'hidden') {
        hiddenGroups.set(name, elements);
        return;
      }

      touchHandlers.add(createTouchHandlers(name, elements, stores));
      dirtyHandlers.add(createDirtyHandler(name, elements, stores)); // Loop over each element and hook in it's handler

      elements.forEach(function (el) {
        if (isGroup) {
          el.setAttribute('data-in-group', groupName);
        }

        setAriaRole(el, elements);
        setAriaStates(el);
        var customBindings = el.dataset.formulaBind;

        if (customBindings) {
          customBindings.split('|').forEach(function (event) {
            return eventHandlers.set(el, createHandler(name, event, el, elements, stores, innerOpt, hiddenGroups));
          });
        } else if (el instanceof HTMLSelectElement) {
          eventHandlers.set(el, createHandler(name, 'change', el, elements, stores, innerOpt, hiddenGroups));
        } else {
          switch (el.type) {
            case 'radio':
            case 'checkbox':
            case 'file':
            case 'range':
            case 'color':
              {
                eventHandlers.set(el, createHandler(name, 'change', el, elements, stores, innerOpt, hiddenGroups));
                break;
              }

            case 'date':
            case 'time':
            case 'week':
            case 'number':
              {
                eventHandlers.set(el, createHandler(name, 'change', el, elements, stores, innerOpt, hiddenGroups));
                eventHandlers.set(el, createHandler(name, 'keyup', el, elements, stores, innerOpt, hiddenGroups));
                break;
              }

            case 'hidden':
              {
                // Ensure no handlers on hidden fields as
                break;
              }

            default:
              eventHandlers.set(el, createHandler(name, 'keyup', el, elements, stores, innerOpt, hiddenGroups));
          }
        }
      });
    }); // If the field has a global store, set the ID

    if (node.id && globalStore) globalStore.set(node.id, stores); // If the HTML element attached is a form, also listen for the submit event

    if (node instanceof HTMLFormElement) {
      submitHandler = createSubmitHandler(stores, node);
      node.addEventListener('submit', submitHandler);
    }

    stores.isFormReady.set(true);
  } // Keep a instance of the passed node around


  var currentNode;
  /**
   * Method called when updating or destoying the form, this removes all existing handlers
   * and cleans up
   */

  function cleanupSubscriptions() {

    __spread(eventHandlers).forEach(function (_a) {
      var _b = __read(_a, 2),
          el = _b[0],
          fn = _b[1];

      el.setCustomValidity('');
      fn();
    });

    __spread(touchHandlers, dirtyHandlers).forEach(function (fn) {
      return fn();
    });

    [eventHandlers, touchHandlers, dirtyHandlers].forEach(function (h) {
      return h.clear();
    });
    if (submitHandler) currentNode.removeEventListener('submit', submitHandler);
  }

  return __assign({
    /**
     * Create an action for the `use` directive
     * @param node
     */
    form: function form(node) {
      currentNode = node;
      bindElements(node, options);
      return {
        destroy: function destroy() {
          cleanupSubscriptions();
          currentNode.id && globalStore && globalStore["delete"](currentNode.id);
        }
      };
    },

    /**
     * Update a form instance , new options can be passed to the form instance, if not it will use the original
     * values passed
     * @param updatedOpts
     */
    updateForm: function updateForm(updatedOpts) {
      stores.isFormReady.set(false);
      cleanupSubscriptions();
      bindElements(currentNode, updatedOpts || initialOptions);
    },

    /**
     * Destroy the form instance
     */
    destroyForm: function destroyForm() {
      stores.isFormReady.set(false);
      cleanupSubscriptions();
      currentNode.id && globalStore && globalStore["delete"](currentNode.id);
    },

    /**
     * Reset the data in the form instance
     */
    resetForm: function resetForm() {
      innerReset();

      __spread(touchHandlers, dirtyHandlers).forEach(function (fn) {
        return fn();
      });

      groupedMap.forEach(function (_a) {
        var _b = __read(_a, 2),
            name = _b[0],
            elements = _b[1];

        touchHandlers.add(createTouchHandlers(name, elements, stores));
        dirtyHandlers.add(createDirtyHandler(name, elements, stores));
      });
    },
    stores: stores
  }, stores);
}

/**
 * A global map of stores for elements with an `id` property and the `use` directive,
 * if no ID is used the store is not added
 * @type Map<string, FormulaStores>
 */

var formulaStores = new Map();
/**
 * The `formula` function returns a form object that can be bound to any HTML
 * element that contains form inputs.  Once bound you can get the current values
 *
 * @param options Optional options that the library supports, none of these options are
 * required to use Formula
 *
 * @returns Formula object containing the current form, function to update or destroy
 * the form and all the stores available for the form
 */

function formula(options) {
  return createForm(options, formulaStores);
}

export { formula };
